import { useEffect, useRef, useState, useCallback } from 'react';
import type { Map as olMap } from 'ol';
import type BaseLayer from 'ol/layer/Base';

type SwipeDirection = 'horizontal' | 'vertical';

export function useSwipe(map: olMap | undefined, selectedTool: any) {
  const [swipeLayer, setSwipeLayer] = useState<BaseLayer | null>(null);
  const [swipeDirection, setSwipeDirection] = useState<SwipeDirection>('vertical');
  const swipeRef = useRef<null | { layer: BaseLayer; position: number; direction: SwipeDirection }>(null);
  const swipeMapRef = useRef<olMap | null>(null);
  const swipeBarRef = useRef<HTMLElement | null>(null);
  const draggingRef = useRef(false);

  const onWindowPointerMoveRef = useRef<((e: PointerEvent) => void) | null>(null);
  const onWindowPointerUpRef = useRef<(() => void) | null>(null);
  const onBarPointerDownRef = useRef<((e: PointerEvent) => void) | null>(null);

  const onSwipePreRender = useCallback((evt: any) => {
    if (!swipeRef.current || !swipeMapRef.current) return;

    const ctx = evt.context;
    const size = swipeMapRef.current.getSize();
    if (!size) return;

    const pixelRatio = window.devicePixelRatio || 1;
    const { position, direction } = swipeRef.current;

    ctx.save();
    ctx.beginPath();

    if (direction === 'vertical') {
      ctx.rect(0, 0, size[0] * position * pixelRatio, size[1] * pixelRatio);
    } else {
      ctx.rect(0, 0, size[0] * pixelRatio, size[1] * position * pixelRatio);
    }

    ctx.clip();
  }, []);

  const onSwipePostRender = useCallback((evt: any) => {
    try {
      evt.context.restore();
    } catch (e) {
    }
  }, []);

  const createSwipeBar = useCallback((mapInstance: olMap, direction: SwipeDirection) => {
    if (swipeBarRef.current) {
      try { mapInstance.getTargetElement().removeChild(swipeBarRef.current); } catch (e) {}
      swipeBarRef.current = null;
    }

    const bar = document.createElement('div');
    bar.style.position = 'absolute';
    bar.style.background = '#2c689c';
    bar.style.display = 'flex';
    bar.style.alignItems = 'center';
    bar.style.justifyContent = 'center';
    bar.style.pointerEvents = 'auto';

    const BAR_SIZE = 10;

    if (direction === 'vertical') {
      bar.style.top = '0';
      bar.style.bottom = '0';
      bar.style.width = `${BAR_SIZE}px`;
      bar.style.cursor = 'ew-resize';
      bar.style.left = '50%';
      bar.style.transform = 'translateX(-50%)';
      bar.style.borderLeft = '1px solid #000';
      bar.style.borderRight = '1px solid #000';
      bar.style.zIndex = '0';
    } else {
      bar.style.left = '0';
      bar.style.right = '0';
      bar.style.height = `${BAR_SIZE}px`;
      bar.style.cursor = 'ns-resize';
      bar.style.top = '50%';
      bar.style.transform = 'translateY(-50%)';
      bar.style.borderTop = '1px solid #000';
      bar.style.borderBottom = '1px solid #000';
      bar.style.zIndex = '0';
    }

    const arrows = document.createElement('div');
    arrows.style.display = 'flex';
    arrows.style.flexDirection = direction === 'vertical' ? 'column' : 'row';
    arrows.style.alignItems = 'center';
    arrows.style.justifyContent = 'center';
    arrows.style.background = 'rgba(0,0,0,0.35)';
    arrows.style.borderRadius = '4px';
    arrows.style.padding = '4px';
    arrows.style.pointerEvents = 'none';

    const leftArrow = document.createElement('span');
    const rightArrow = document.createElement('span');

    if (direction === 'vertical') {
      leftArrow.innerText = '<';
      rightArrow.innerText = '>';
      leftArrow.style.fontSize = '26px';
      rightArrow.style.fontSize = '26px';
      leftArrow.style.lineHeight = '14px';
      rightArrow.style.lineHeight = '14px';
    } else {
      leftArrow.innerText = '˄';
      rightArrow.innerText = '˅';
      leftArrow.style.fontSize = '40px';
      rightArrow.style.fontSize = '40px';
      leftArrow.style.lineHeight = '10px';
      rightArrow.style.lineHeight = '10px';
    }

    leftArrow.style.color = '#fff';
    rightArrow.style.color = '#fff';

    arrows.appendChild(leftArrow);
    arrows.appendChild(rightArrow);
    bar.appendChild(arrows);

    onBarPointerDownRef.current = (e: PointerEvent) => {
      e.preventDefault();
      draggingRef.current = true;
    };
    bar.addEventListener('pointerdown', onBarPointerDownRef.current);

    onWindowPointerUpRef.current = () => {
      draggingRef.current = false;
    };
    window.addEventListener('pointerup', onWindowPointerUpRef.current);

    onWindowPointerMoveRef.current = (e: PointerEvent) => {
      if (!draggingRef.current || !swipeRef.current || !swipeMapRef.current) return;

      const rect = swipeMapRef.current.getTargetElement().getBoundingClientRect();
      let pos = 0;

      if (direction === 'vertical') {
        const barHalf = bar.offsetWidth / 2;
        pos = (e.clientX - rect.left - barHalf) / rect.width;
        pos = Math.max(0, Math.min(1, pos));
        bar.style.left = `${pos * 100}%`;
      } else {
        const barHalf = bar.offsetHeight / 2;
        pos = (e.clientY - rect.top - barHalf) / rect.height;
        pos = Math.max(0, Math.min(1, pos));
        bar.style.top = `${pos * 100}%`;
      }

      if (swipeRef.current) {
        swipeRef.current.position = pos;
      }
      swipeMapRef.current.render();
    };
    window.addEventListener('pointermove', onWindowPointerMoveRef.current);

    swipeBarRef.current = bar;
    mapInstance.getTargetElement().appendChild(bar);
    mapInstance.getTargetElement().style.overflow = 'hidden';
  }, []);

  const removeSwipeBar = useCallback((mapInstance: olMap | null) => {
    if (!mapInstance) return;
    if (!swipeBarRef.current) return;

    try {
      mapInstance.getTargetElement().removeChild(swipeBarRef.current);
    } catch (e) {}

    if (onBarPointerDownRef.current) {
      try { swipeBarRef.current.removeEventListener('pointerdown', onBarPointerDownRef.current); } catch (e) { /* noop */ }
      onBarPointerDownRef.current = null;
    }
    if (onWindowPointerMoveRef.current) {
      window.removeEventListener('pointermove', onWindowPointerMoveRef.current);
      onWindowPointerMoveRef.current = null;
    }
    if (onWindowPointerUpRef.current) {
      window.removeEventListener('pointerup', onWindowPointerUpRef.current as any);
      onWindowPointerUpRef.current = null;
    }

    swipeBarRef.current = null;
  }, []);

  // ativa swipe
  const enableSwipe = useCallback((mapInstance: olMap, layer: BaseLayer, direction: SwipeDirection) => {
    swipeMapRef.current = mapInstance;
    swipeRef.current = { layer, position: 0.5, direction };

    try {
      (layer as any).on('prerender', onSwipePreRender);
      (layer as any).on('postrender', onSwipePostRender);
    } catch (err) {
    }

    createSwipeBar(mapInstance, direction);
    mapInstance.render();
  }, [createSwipeBar, onSwipePostRender, onSwipePreRender]);

  // desativa swipe e limpa barra
  const disableSwipe = useCallback(() => {
    if (!swipeRef.current || !swipeMapRef.current) return;

    try {
      (swipeRef.current.layer as any).un('prerender', onSwipePreRender);
      (swipeRef.current.layer as any).un('postrender', onSwipePostRender);
    } catch (err) {
    }

    removeSwipeBar(swipeMapRef.current);
    swipeRef.current = null;
    swipeMapRef.current?.render();
  }, [onSwipePostRender, onSwipePreRender, removeSwipeBar]);

  const handleSetSwipeLayer = useCallback((id: string) => {
    if (!map) return;
    if (id === 'disable') {
      setSwipeLayer(null);
      disableSwipe();
      return;
    }

    const layer = map.getLayers().getArray().find(l => {
      const props = l.getProperties?.() ?? {};
      return props.uuid === id || props.layertitle === id;
    }) as BaseLayer | undefined;

    if (!layer) return;

    setSwipeLayer(layer);
    disableSwipe();
    enableSwipe(map, layer, swipeDirection);
  }, [map, disableSwipe, enableSwipe, swipeDirection]);

  useEffect(() => {
    if (!map) return;

    if (String(selectedTool?.action) === 'layerSwipe' && swipeLayer) {
      disableSwipe();
      enableSwipe(map, swipeLayer, swipeDirection);
    } else {
      disableSwipe();
    }

    return () => {
      disableSwipe();
    };
  }, [map, selectedTool?.action, swipeLayer, swipeDirection, disableSwipe, enableSwipe]);

  useEffect(() => {
    if (!map) return;
    if (!swipeLayer) return;

    disableSwipe();
    enableSwipe(map, swipeLayer, swipeDirection);
  }, [swipeDirection, map, swipeLayer, disableSwipe, enableSwipe]);

  useEffect(() => {
    return () => {
      try {
        disableSwipe();
      } catch (e) {}
    };
  }, []);

  return {
    swipeLayer,
    handleSetSwipeLayer,
    swipeDirection,
    setSwipeDirection,
  };
}